// components/asset-management/workbookEngine.ts
// A workbook-scoped formula engine for imported multi-sheet models. react-
// spreadsheet evaluates one sheet at a time and can't resolve cross-sheet
// references like ='01_Budget'!C100, so we compute the whole workbook here with
// fast-formula-parser (MIT): its onCell/onRange resolve cell references across
// ALL sheets (recursively, memoised, cycle-guarded). The grid then displays
// these computed values. Registers IRR/NPV/EQMULT like the in-grid parser.
'use client';

import FormulaParser from 'fast-formula-parser';
import { extraFormulaFunctions, desugarLet, coerceFormulaInput, rewriteArray } from './formulaFunctions';

export interface WorkbookSheet {
  name: string;
  /** sparse cell map keyed "row,col" (0-indexed) -> raw value (a "=formula" or a literal) */
  cells: Record<string, string>;
}

/** sheetNameLowercased -> ("row,col" -> computed value) */
export type ComputedWorkbook = Record<string, Record<string, string | number | boolean | null>>;

/** Compute every formula cell across all sheets, resolving cross-sheet references. */
export function computeWorkbook(sheets: ReadonlyArray<WorkbookSheet>): ComputedWorkbook {
  const byName = new Map<string, Record<string, string>>();
  for (const s of sheets) byName.set(s.name.toLowerCase(), s.cells);

  const out: ComputedWorkbook = {};
  const computing = new Set<string>();
  let currentSheet = sheets[0]?.name.toLowerCase() ?? '';

  const toLiteral = (raw: string): string | number | null => {
    if (raw === '') return null;
    const t = raw.trim();
    const n = Number(t);
    return t !== '' && Number.isFinite(n) ? n : raw;
  };

  const getCell = (sheetLower: string, r: number, c: number): string | number | boolean | null => {
    if (r < 0 || c < 0) return null;
    const key = `${r},${c}`;
    const done = out[sheetLower];
    if (done && key in done) return done[key]!;
    const cells = byName.get(sheetLower);
    if (!cells) return null;
    const raw = cells[key];
    if (raw === undefined || raw === '') return null;

    const guard = `${sheetLower}!${key}`;
    if (computing.has(guard)) return 0; // circular reference -> treat as 0

    let val: string | number | boolean | null;
    if (typeof raw === 'string' && raw.startsWith('=')) {
      // Some imported cells are mistyped TEXT labels stored as formulas (e.g. "=+ Uplift de loyer").
      // These aren't valid formulas (Excel itself shows #NAME?). Detect prose — starts with a letter,
      // contains a space, no range ":" and no `name(` call pattern — and just display it as text.
      const deSigned = raw.slice(1).replace(/^\s*[+-]\s*/, '');
      if (/^[A-Za-zÀ-ÿ]/.test(deSigned) && /\s/.test(deSigned) && deSigned.indexOf(':') === -1 && !/[A-Za-zÀ-ÿ]\(/.test(deSigned)) {
        val = deSigned;
      } else {
        const prev = currentSheet;
        currentSheet = sheetLower;
        computing.add(guard);
        try {
          // desugarLet (LET inlining) then rewriteArray (array operators -> _AOP/_AROW) before parse.
          const res = evalFormula(rewriteArray(desugarLet(raw.slice(1))), { sheet: sheetLower, row: r + 1, col: c + 1 });
          if (res === null || res === undefined) val = null;
          else if (Array.isArray(res)) val = (res[0]?.[0] ?? null) as string | number | null;
          else if (typeof res === 'object') val = String(res); // FormulaError -> "#VALUE!" etc.
          else val = res as string | number | boolean;
        } catch {
          val = '#ERROR';
        } finally {
          computing.delete(guard);
          currentSheet = prev;
        }
      }
    } else {
      val = toLiteral(raw);
    }
    (out[sheetLower] ??= {})[key] = val;
    return val;
  };

  // fast-formula-parser uses a SINGLE mutable parser instance. Evaluating a cell whose formula
  // references a not-yet-computed cell re-enters parse() (parse -> onRange -> getCell -> parse),
  // which corrupts the shared chevrotain state and throws "Cannot read properties of undefined".
  // So we keep a POOL of parser instances and borrow a fresh one per (possibly re-entrant)
  // evaluation; the pool grows only to the max dependency-recursion depth, then is reused.
  const pool: FormulaParser[] = [];
  const makeParser = (): FormulaParser =>
    new FormulaParser({
      // Coerce formatted-text literals ("12,500,000", "18.0%", "07/2026") to numbers when a
      // formula reads them, so cross-cell math works while the grid still displays the text.
      onCell: (ref: { sheet?: string; row: number; col: number }) =>
        coerceFormulaInput(getCell((ref.sheet ?? currentSheet).toLowerCase(), ref.row - 1, ref.col - 1)) as string | number | boolean | null,
      onRange: (ref: { sheet?: string; from: { row: number; col: number }; to: { row: number; col: number } }) => {
        const sl = (ref.sheet ?? currentSheet).toLowerCase();
        const arr: (string | number | boolean | null)[][] = [];
        for (let r = ref.from.row; r <= ref.to.row; r += 1) {
          const row: (string | number | boolean | null)[] = [];
          for (let c = ref.from.col; c <= ref.to.col; c += 1) row.push(coerceFormulaInput(getCell(sl, r - 1, c - 1)) as string | number | boolean | null);
          arr.push(row);
        }
        return arr;
      },
      // IRR/NPV/EQMULT, the real MAX/MIN/MEDIAN/CHOOSE/MATCH and the array helpers _AOP/_AROW —
      // the same map the in-grid parser registers, so both engines evaluate identically.
      functions: { ...extraFormulaFunctions },
    });
  const evalFormula = (formula: string, position: { sheet: string; row: number; col: number }): unknown => {
    const parser = pool.pop() ?? makeParser();
    try {
      return parser.parse(formula, position);
    } finally {
      pool.push(parser);
    }
  };

  // Eagerly evaluate every stored cell so the result map is complete.
  for (const s of sheets) {
    const sl = s.name.toLowerCase();
    for (const key of Object.keys(s.cells)) {
      const parts = key.split(',');
      getCell(sl, Number(parts[0]), Number(parts[1]));
    }
  }
  return out;
}
